import { useEffect, useState } from "react"

import { EmbeddedCheckout, EmbeddedCheckoutProvider } from "@stripe/react-stripe-js"
import { loadStripe } from "@stripe/stripe-js"
import { Alert, Button, Card, Divider, Flex, Layout, Radio, Space, Spin, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useLocation } from "react-router"
import { useNavigate } from "react-router-dom"

import { AdyenDropIn } from "../../../Components/AdyenDropIn/AdyenDropIn"
import ButtonComponent from "../../../Components/Button"
import Icon from "../../../Icon"
import Images from "../../../Images"
import { getJourneyState, JOURNEY_FLOWS } from "../../../Utils/onboarding/getJourneyState"
import {
  getBusinessPathByStepId,
  getOnboardingPathByStepId
} from "../../../Utils/onboarding/onboardingFlow"
import {
  CHOICE_FLOWS,
  hasOnboardingProgress,
  resolveProductChoice
} from "../../../Utils/onboarding/productChoice"
import {
  getApplicableSteps,
  previousApplicableStepId
} from "../../../Utils/onboarding/stepSequence"
import { createAccountStepPath, onboardingStepPath, useQuery } from "../../../Utils/utils"
import { BA_STEPS } from "../../GettingStartedPage/utils/businessSteps"
import { OB_STEPS } from "../../GettingStartedPage/utils/onboardingSteps"
import { changeNextCreateAccountStep } from "../../User/redux/user.actions"
import {
  attachPayment,
  changeOnboardingStep,
  getUserInfo,
  trackEvent,
  userSave
} from "../../User/redux/user.thunks"
import { updateCompletedStep } from "../../User/services"
import MobileCtaButtons from "../PaymentConnectPage/components/MobileCtaButtons"

import "./styles.less"

const { Text } = Typography
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
const environment = process.env.REACT_APP_ENVIRONMENT === "prod" ? "live" : "test"
const OnboardingCardInfoPage = (props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { pathname, search } = useLocation()
  const navigate = useNavigate()
  let query = useQuery(search)
  let sessionId = query.get("session_id")
  const cardAdded = query.get("card")

  const { fetching, accessToken } = useSelector((state) => state.auth)
  const isMobile = useSelector((state) => state.nav.isMobile)
  const theme = useSelector((state) => state.nav.theme)

  const nextCreateAccountStep = useSelector((state) => state.user.nextCreateAccountStep)
  const businessAccount = useSelector((state) => state.user.businessAccount)
  const businessAccounts = useSelector((state) => state.user.businessAccounts)
  const checkoutSessionFetching = useSelector((state) => state.user.fetching)
  const userInfo = useSelector((state) => state.auth.userInfo)
  const userState = useSelector((state) => state.user)

  const [clientSecret, setClientSecret] = useState("")
  const [customer, setCustomer] = useState(null)
  const [selectPayment, setSelectPayment] = useState(null)
  const [adyenData, setAdyenData] = useState({})
  const [provider, setProvider] = useState()
  const unsupportedCardsMessage =
    provider === "adyen"
      ? t("At this time, we do not accept American Express.")
      : t("At this time, we do not accept American Express and Discover.")
  const journey = getJourneyState({
    userInfo,
    userState,
    pathname
  })
  const invitedUserOnboarding = journey.flow === JOURNEY_FLOWS.INVITED_CREATE_BUSINESS
  const paymentServiceBusinessAccountId =
    businessAccount?.payment_service_business_account_id || null
  const isShowForm = selectPayment === "new_card" && nextCreateAccountStep
  const isOnboarding = !nextCreateAccountStep && !selectPayment
  const hasOnboarding = journey.flow === JOURNEY_FLOWS.ONBOARDING
  const shouldShowPayment =
    clientSecret &&
    !sessionId &&
    (nextCreateAccountStep ? selectPayment !== null && selectPayment === "new_card" : true)

  const isNewBusiness = pathname?.includes("/create-account")

  useEffect(() => {
    if (!cardAdded) return

    handleUpdateSteps()
    handleUpdateStatistics()
  }, [cardAdded])

  const handleNavigate = () => {
    if (isNewBusiness) {
      navigate(createAccountStepPath[7])
      return
    }
    navigate(onboardingStepPath[2])
  }

  useEffect(() => {
    if (sessionId && accessToken) {
      dispatch(attachPayment({ checkout_session_id: sessionId }))
    }
    document.title = "Card Info - Chargeback"
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [accessToken, sessionId])

  useEffect(() => {
    if (paymentServiceBusinessAccountId && !clientSecret && !sessionId) {
      // Create a Checkout Session as soon as the page loads
      const returnUrl = `${process.env.REACT_APP_BASE_URI}${
        hasOnboarding ? onboardingStepPath[OB_STEPS.CARD] : createAccountStepPath[BA_STEPS.CARD]
      }?card=added`
      fetch(`${process.env.REACT_APP_BASE_API_ROUTE}/payments/create-checkout-session/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `JWT ${accessToken}`
        },
        body: JSON.stringify({
          payment_service_business_account_id: paymentServiceBusinessAccountId,
          business_account_id: businessAccount.id,
          return_url: returnUrl,
          // process.env.REACT_APP_BASE_URI +
          // pathname +
          // "/success/?session_id={CHECKOUT_SESSION_ID}",
          is_onboarding: true
        })
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.provider === "adyen") {
            setAdyenData(data.adyen_session)
          }
          setProvider(data.provider)
          setClientSecret(data?.["checkout_session"]?.client_secret)
          setCustomer(data?.["checkout_session"]?.customer)
        })
        .catch((error) => {
          console.error("Error:", error)
        })
    }
  }, [paymentServiceBusinessAccountId])

  const options = { clientSecret }

  const handleAttachCard = () => {
    if (selectPayment === null || selectPayment === "new_card") return null

    dispatch(
      attachPayment({
        existing_card_id: selectPayment,
        new_business_account_to_attach: businessAccount.id
      })
    )
  }

  const onSuccess = (res) => {
    dispatch(changeOnboardingStep({ next_step: 8 }))
    fetch(`${process.env.REACT_APP_BASE_API_ROUTE}/payments/finalize-onboarding/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${accessToken}`
      },
      body: JSON.stringify({
        adyen_session_id: adyenData.id,
        adyen_session_result: res.sessionResult
      })
    })
      .then(async () => {
        handleUpdateSteps()
        handleUpdateStatistics()
      })
      .catch((error) => console.error(error))
  }

  function handleUpdateSteps() {
    dispatch(getUserInfo())
    if (invitedUserOnboarding) {
      updateCompletedStep({
        step: 5,
        business_account_id: businessAccount?.id
      })
      dispatch(changeNextCreateAccountStep(8))
      dispatch(userSave({ next_step: 8 }))
      navigate(createAccountStepPath[6])
    } else {
      dispatch(changeNextCreateAccountStep(null))
      dispatch(userSave({ next_step: 8 }))
      navigate(onboardingStepPath[8])
    }
  }
  // TODO: temporary solution — move logic to saga apiAttachPayment
  const handleUpdateStatistics = () => {
    window.dataLayer = window?.dataLayer || []
    window.dataLayer.push({
      event: "account_created",
      email: userInfo.email
    })
    //make request for check if need to push event in window.datalayer
    dispatch(trackEvent({ event_type: "fifth_step" }))
  }

  const handleMobileContinue = () => {
    if (provider === "adyen" && (isShowForm || isOnboarding)) {
      document.querySelector(".adyen-checkout__button--pay")?.click()
      return
    }
    if (provider === "stripe" && shouldShowPayment) {
      document.getElementById("checkout")?.querySelector('button[type="submit"]')?.click()
      return
    }
    handleAttachCard()
  }

  const handleBack = () => {
    const isBusinessFlow = journey?.isBusinessFlow
    const currentStep = journey?.currentStep

    if (currentStep > OB_STEPS.CARD && !isBusinessFlow) {
      return navigate(onboardingStepPath[currentStep])
    }

    const flow = isNewBusiness ? CHOICE_FLOWS.BUSINESS : CHOICE_FLOWS.ONBOARDING
    const choice = resolveProductChoice({
      flow,
      hasProgress: hasOnboardingProgress({ flow, userInfo, nextCreateAccountStep })
    })
    const stepMap = isNewBusiness ? BA_STEPS : OB_STEPS
    const previousStepId = previousApplicableStepId({
      stepId: stepMap.CARD,
      steps: getApplicableSteps({ choice }),
      stepMap
    })

    navigate(
      isNewBusiness
        ? getBusinessPathByStepId(previousStepId)
        : getOnboardingPathByStepId(previousStepId)
    )
  }
  return (
    <Layout>
      <Spin size="large" spinning={fetching || checkoutSessionFetching}>
        <div className="add-card-info-ob-step-content">
          {!isMobile && (
            <Button
              className="back-btn"
              type="text"
              icon={<Icon type="arrow_left" />}
              onClick={handleBack}
            >
              Back
            </Button>
          )}
          <div className="content-main sign-up">
            <Card
              className={`login-card ${isMobile && "mobile-padding"}`}
              style={{ margin: "0 0 auto", width: "100%" }}
            >
              {nextCreateAccountStep ? (
                <>
                  <h2
                    className="card-info_title"
                    style={{
                      textAlign: "left",
                      marginBottom: 8,
                      fontSize: isMobile && 18,
                      lineHeight: isMobile && "24px"
                    }}
                  >
                    {t("Add payment method")}
                  </h2>
                  <h4 className="card-info_subtitle card-subtitle-signup">
                    {t("Use a card from another previously created Business account.")}
                  </h4>
                  <ul style={{ marginBottom: 24 }}>
                    {[
                      ...businessAccounts?.filter((ba) => ba?.card && ba?.card?.id),
                      {
                        id: "new_card",
                        card: { id: "new_card" },
                        name: "Another card"
                      }
                    ].map((ba) => (
                      <li key={ba.id} style={{ padding: "4px 0" }}>
                        <Flex gap={4} align="center">
                          <Text style={{ width: 154 }}>
                            {ba.id === "new_card" ? t(ba.name) : ba.name}
                          </Text>
                          {ba?.card?.last4 && (
                            <Flex gap={4} align={"center"}>
                              <Divider type="vertical" />
                              {ba?.card?.brand && (
                                <img
                                  src={
                                    Images[`${ba?.card?.brand}${theme === "dark" ? "_dark" : ""}`]
                                  }
                                  alt={ba?.card?.brand}
                                />
                              )}
                              {ba?.card?.["last4"] && (
                                <>
                                  <Text>****</Text>
                                  <Text>{ba?.card?.["last4"]}</Text>
                                </>
                              )}
                            </Flex>
                          )}

                          <Radio
                            value={ba?.card?.id}
                            checked={selectPayment === ba?.card?.id}
                            onChange={() => setSelectPayment(ba?.card?.id)}
                            style={{ marginLeft: "auto" }}
                          />
                        </Flex>
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}

              {selectPayment !== null && selectPayment !== "new_card" ? (
                <ButtonComponent size="large" type="primary" onClick={handleAttachCard}>
                  {t("Continue")}
                </ButtonComponent>
              ) : null}

              {(
                nextCreateAccountStep
                  ? selectPayment !== null && selectPayment === "new_card"
                  : true
              ) ? (
                <>
                  {nextCreateAccountStep ? null : (
                    <h2
                      className="card-info_title"
                      style={{
                        textAlign: "left",
                        marginBottom: 8,
                        fontSize: isMobile && 18,
                        lineHeight: isMobile && "24px"
                      }}
                    >
                      {t("Add payment method")}
                    </h2>
                  )}
                  <h4 className="card-subtitle-signup card-info_subtitle">
                    {t("Enter your card details to activate your Chargeback account.")}
                  </h4>
                  <Alert
                    className="primary"
                    type="info"
                    icon={<Icon type="circle_info" style={{ color: "#1448CB" }} />}
                    showIcon
                    message={
                      <Space direction="vertical">
                        <span>
                          {t(
                            "No charges will be applied. Your bank may place a temporary authorization to verify your card."
                          )}
                        </span>
                        <span>{unsupportedCardsMessage}</span>
                      </Space>
                    }
                    style={{ marginBottom: 16 }}
                  />
                </>
              ) : null}
              <div id="checkout" style={{ margin: "auto" }}>
                {shouldShowPayment && provider === "stripe" && (
                  <div className="iframe-wrapper">
                    <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
                      <EmbeddedCheckout />
                    </EmbeddedCheckoutProvider>
                  </div>
                )}
                {provider === "adyen" && (isShowForm || isOnboarding) && (
                  <AdyenDropIn
                    session={{
                      id: adyenData.id,
                      session: adyenData.sessionData
                    }}
                    clientKey={adyenData.clientKey}
                    amount={adyenData.amount}
                    environment={environment}
                    countryCode={adyenData.countryCode}
                    locale={adyenData.shopperLocale}
                    recurringProcessingModel={adyenData.recurringProcessingModel}
                    onSuccess={onSuccess}
                    onError={(e) => {
                      console.error(e)
                    }}
                  />
                )}
              </div>

              {customer && !clientSecret && (
                <ButtonComponent
                  size="large"
                  type="primary"
                  onClick={() => dispatch(getUserInfo())}
                >
                  Refresh user info
                </ButtonComponent>
              )}
            </Card>
          </div>
          {isMobile && (
            <div className="pp-sticky-cta">
              <MobileCtaButtons
                isMobile={isMobile}
                onSaveForLater={handleNavigate}
                onBack={handleBack}
              />
              <ButtonComponent
                size="large"
                type="primary"
                onClick={handleMobileContinue}
                className={"flex center"}
                style={{ width: "100%" }}
              >
                {t("Continue")}
              </ButtonComponent>
            </div>
          )}
        </div>
      </Spin>
    </Layout>
  )
}

export default OnboardingCardInfoPage
